import React from 'react';
import { Shield, Heart, Plus, CheckCircle } from 'lucide-react';
import { CoverageHero } from '../components/CoverageHero';
import { CoverageCTA } from '../components/CoverageCTA';

const HealthSupplemental = () => {
  return (
    <div className="min-h-screen">
      <CoverageHero
        title="Health and Supplemental Coverage"
        subtitle="ACA Marketplace plans, hospital indemnity, accident coverage, critical illness, and supplemental protection. Health coverage with purpose."
      />

      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-6">Comprehensive Health Coverage Options</h2>
            <p className="text-base md:text-lg text-gray-700">From ACA Marketplace plans to supplemental insurance, we help you build comprehensive protection.</p>
          </div>

          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white border-t-4 border-brand-blue rounded-lg p-6 shadow-md">
              <div className="bg-brand-blue text-white w-14 h-14 rounded-full flex items-center justify-center mb-4"><Heart size={28} /></div>
              <h3 className="text-xl font-bold text-brand-blue mb-3">ACA Marketplace Plans</h3>
              <p className="text-gray-700 mb-4">Affordable Care Act plans with comprehensive medical coverage. Many qualify for subsidies.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-600"><CheckCircle size={14} className="text-brand-orange" /> Bronze, Silver, Gold tiers</li>
                <li className="flex items-center gap-2 text-sm text-gray-600"><CheckCircle size={14} className="text-brand-orange" /> Premium tax credits available</li>
                <li className="flex items-center gap-2 text-sm text-gray-600"><CheckCircle size={14} className="text-brand-orange" /> No pre-existing condition exclusions</li>
              </ul>
            </div>

            <div className="bg-white border-t-4 border-brand-orange rounded-lg p-6 shadow-md">
              <div className="bg-brand-orange text-white w-14 h-14 rounded-full flex items-center justify-center mb-4"><Shield size={28} /></div>
              <h3 className="text-xl font-bold text-brand-blue mb-3">Hospital Indemnity</h3>
              <p className="text-gray-700 mb-4">Fixed amount for hospital stays regardless of actual costs.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-600"><CheckCircle size={14} className="text-brand-orange" /> Cash benefits paid to you</li>
                <li className="flex items-center gap-2 text-sm text-gray-600"><CheckCircle size={14} className="text-brand-orange" /> Supplements primary insurance</li>
                <li className="flex items-center gap-2 text-sm text-gray-600"><CheckCircle size={14} className="text-brand-orange" /> Covers admission, daily stays, ICU</li>
              </ul>
            </div>

            <div className="bg-white border-t-4 border-brand-blue rounded-lg p-6 shadow-md">
              <div className="bg-brand-blue text-white w-14 h-14 rounded-full flex items-center justify-center mb-4"><Plus size={28} /></div>
              <h3 className="text-xl font-bold text-brand-blue mb-3">Accident and Critical Illness</h3>
              <p className="text-gray-700 mb-4">Coverage for unexpected accidents and critical illness diagnoses.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-600"><CheckCircle size={14} className="text-brand-orange" /> Accident coverage for injuries</li>
                <li className="flex items-center gap-2 text-sm text-gray-600"><CheckCircle size={14} className="text-brand-orange" /> Critical illness lump-sum benefits</li>
                <li className="flex items-center gap-2 text-sm text-gray-600"><CheckCircle size={14} className="text-brand-orange" /> Gap coverage for high deductibles</li>
              </ul>
            </div>

            <div className="bg-white border-t-4 border-brand-orange rounded-lg p-6 shadow-md">
              <div className="bg-brand-orange text-white w-14 h-14 rounded-full flex items-center justify-center mb-4"><Shield size={28} /></div>
              <h3 className="text-xl font-bold text-brand-blue mb-3">Dental, Vision and More</h3>
              <p className="text-gray-700 mb-4">Standalone dental, vision, and supplemental plans.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-600"><CheckCircle size={14} className="text-brand-orange" /> Preventive dental care</li>
                <li className="flex items-center gap-2 text-sm text-gray-600"><CheckCircle size={14} className="text-brand-orange" /> Eye exams and glasses</li>
                <li className="flex items-center gap-2 text-sm text-gray-600"><CheckCircle size={14} className="text-brand-orange" /> Short-term health insurance</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-brand-blue mb-8 text-center">Health Coverage FAQs</h2>
            <div className="space-y-4 max-w-3xl mx-auto">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-bold text-brand-blue mb-2">When is ACA open enrollment?</h3>
                <p className="text-gray-700 text-sm">The ACA Open Enrollment Period typically runs from November 1 through January 15.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-bold text-brand-blue mb-2">What is hospital indemnity insurance?</h3>
                <p className="text-gray-700 text-sm">Hospital indemnity pays a fixed benefit when hospitalized. Cash is paid directly to you.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="font-bold text-brand-blue mb-2">Can I have supplemental insurance with my employer plan?</h3>
                <p className="text-gray-700 text-sm">Yes. Supplemental insurance works alongside your primary health insurance to fill gaps.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CoverageCTA heading="Compare Health Coverage Options" />
    </div>
  );
};

export default HealthSupplemental;
